import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    FC,
    MouseEvent
} from "react";

import { css, cssMap } from "@compiled/react";

import Spinner from "@atlaskit/spinner";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import {
    absoluteToRelativeHref,
    isLocalizableHref,
    useLocalizedHref
} from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export type ButtonSize = "large" | "small";
export type ButtonColor = "blue" | "yellow" | "white";
export type ButtonColorAgainst = "white" | "black" | "other";
export type ButtonVariant = "primary" | "primary_alt" | "secondary";

// CSS Map does not allow tokens
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buttonPrimaryStyles: { [key: string]: any } = cssMap({
    primary: {
        "color": "#ffffff",
        "backgroundColor": "#1868DB",
        "border": "2px solid transparent",

        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#123263"
        }
    },
    primary_alt: {
        "color": "#101214",
        "backgroundColor": "#FCA700",
        "border": "2px solid transparent",

        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#FFE48F"
        }
    },
    secondary_against_white: {
        "color": "#101214",
        "backgroundColor": "transparent",
        "border": "2px solid #101214",

        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#100F0E10"
        }
    },
    secondary_against_black: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",

        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#2B2C2F"
        }
    },
    secondary_against_other: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",

        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            background: "#2B2C2F33",
            backgroundBlendMode: "multiply"
        }
    }
});

const baseStyles = css({
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "inline-flex",
    "fontWeight": 500,
    "fontFamily": wacToken("font.charlie.text"),
    "textAlign": "center",
    "textDecoration": "none",
    "borderRadius": "40px",

    "&:hover": {
        cursor: "pointer"
    },

    "&:focus-visible": {
        outline: "none"
    }
});

// CSS Map does not allow tokens
// Focus styles map for different background types
const focusStyles = cssMap({
    lightBackground: {
        "&:focus-visible": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            outline: "2px solid #1868DB",
            outlineOffset: "4px"
        }
    },
    otherBackground: {
        "&:focus-visible": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            outline: "2px solid #FFFFFF",
            outlineOffset: "4px"
        }
    }
});

// CSS Map does not allow tokens
const fontAndHeightStyles = cssMap({
    large: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        height: "min-content",
        minHeight: "40px"
    },
    small: {
        fontSize: "1rem",
        lineHeight: "1.25rem",
        height: "min-content",
        minHeight: "36px"
    }
});

const disabledStyles = css({
    backgroundColor: wacToken("color.neutral.light400"),
    color: wacToken("color.neutral.light0"),
    borderColor: "transparent",
    ["&:hover"]: {
        cursor: "not-allowed",
        backgroundColor: wacToken("color.neutral.light400"),
        color: wacToken("color.neutral.light0"),
        borderColor: "transparent"
    }
});

const loadingStyles = css({
    ["&:hover"]: {
        cursor: "not-allowed",
        backgroundColor: "none"
    }
});

const largeResponsiveStyles = css({
    padding: `${token("space.075")} 22px ${token("space.100")}`,
    minWidth: "120px",
    maxWidth: "368px"
});

const smallResponsiveStyles = css({
    padding: `${token("space.075")} 22px`,
    minWidth: "100px",
    maxWidth: "268px"
});

export type ButtonProps = {
    size?: ButtonSize;
    variant?: ButtonVariant;
    colorAgainst?: ButtonColorAgainst;
    className?: string;
    dataEventProps?: DataEventProps;
    isLoading?: boolean;
    isDisabled?: boolean;
} & (
    | DetailedHTMLProps<
          ButtonHTMLAttributes<HTMLButtonElement>,
          HTMLButtonElement
      >
    | DetailedHTMLProps<
          AnchorHTMLAttributes<HTMLAnchorElement>,
          HTMLAnchorElement
      >
);

function isLinkArgs(
    props: unknown
): props is AnchorHTMLAttributes<HTMLAnchorElement> {
    return "href" in (props as AnchorHTMLAttributes<HTMLAnchorElement>);
}

const isSignInUrl = (href: string) => {
    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    // Check if href matches sign-in URL with or without application parameter, or is start-trial
    return (
        href === signInUrl ||
        href.startsWith(signInUrl + "&application=") ||
        href === "#start-trial"
    );
};

const isNonSigninOrWacUrl = (href: string) => {
    return !isLocalizableHref(href) && !isSignInUrl(href);
};

const getButtonVariant = (
    variant: ButtonVariant,
    colorAgainst: ButtonColorAgainst | undefined
): string => {
    if (variant === "primary" || variant === "primary_alt") {
        return variant;
    }
    if (colorAgainst === undefined) {
        return "secondary_against_white";
    }

    return `secondary_against_${colorAgainst}`;
};

const isLightBackground = (
    colorAgainst: ButtonColorAgainst | undefined
): boolean => {
    return colorAgainst === "white" || colorAgainst === undefined;
};

export const Button: FC<ButtonProps> = ({
    variant = "primary",
    size = "large",
    colorAgainst,
    className,
    children,
    dataEventProps,
    isLoading,
    isDisabled = false,
    ...props
}: // eslint-disable-next-line complexity
ButtonProps) => {
    const buttonSize = variant === "secondary" ? "small" : size;

    const isLink = isLinkArgs(props);

    const fontAndHeightStyle = fontAndHeightStyles[buttonSize];

    const buttonVariant = getButtonVariant(variant, colorAgainst);
    const variantStyles = buttonPrimaryStyles[buttonVariant];
    const focusStylesKey = isLightBackground(colorAgainst)
        ? "lightBackground"
        : "otherBackground";
    const buttonFocusStyles = focusStyles[focusStylesKey];

    const handleOnClick = (
        e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>
    ) => {
        if (isDisabled) {
            e.preventDefault();
        } else {
            if (props.onClick) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                props.onClick(e as any);
            }
        }
    };

    const cssList = [
        baseStyles,
        variantStyles,
        fontAndHeightStyle,
        buttonFocusStyles,
        isDisabled && !isLoading && disabledStyles,
        isLoading && loadingStyles,
        buttonSize === "large" && largeResponsiveStyles,
        buttonSize === "small" && smallResponsiveStyles
    ];

    const localizedHref = useLocalizedHref(
        absoluteToRelativeHref(isLink ? props.href ?? "" : "")
    );

    if (isLink) {
        return (
            <a
                className={className}
                css={cssList}
                {...props}
                {...dataEventProps}
                onClick={handleOnClick}
                href={localizedHref}
                target={
                    typeof props.target === "string"
                        ? props.target
                        : props.href &&
                          /* redirecting to new tab if the href is not on WAC and not a Signin link */
                          isNonSigninOrWacUrl(props.href)
                        ? "_blank"
                        : ""
                }
                rel={
                    props.href && isNonSigninOrWacUrl(props.href)
                        ? "noreferrer"
                        : ""
                }
            >
                {children}
            </a>
        );
    }

    // Primary Blue and Secondary (against anything other than white) should have a white spinner
    const spinnerAppearance =
        variant === "primary" ||
        (variant === "secondary" && !!colorAgainst && colorAgainst !== "white")
            ? "invert"
            : "inherit";

    return (
        <button
            className={className}
            disabled={isLoading || isDisabled}
            css={cssList}
            type={
                "button" /* this default value is overridable within the following props */
            }
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            {...dataEventProps}
            onClick={handleOnClick}
        >
            {isLoading ? (
                <Spinner
                    interactionName="load"
                    label="Loading"
                    appearance={spinnerAppearance}
                    size={buttonSize === "small" ? "small" : "medium"}
                />
            ) : (
                children
            )}
        </button>
    );
};
